import { attr, belongsTo, hasMany } from "@ember-data/model";
import type ModelRegistry from "@ember-data/types/registries/model";
import { get } from "@ember/object";
import { service } from "@ember/service";
import { htmlSafe } from "@ember/template";
import { isBlank } from "@ember/utils";
import { cached } from "@glimmer/tracking";
import dayjs from "dayjs";
import type AbilitiesService from "ember-can/services/abilities";
import type Features from "ember-feature-flags";
import type PatientAbility from "parog-web/abilities/patient";
import type { INS, InsOid } from "parog-web/cnam/types";
import { InsSex, InsStatus } from "parog-web/cnam/types";
import type AbdominalAreaDeclaredJournalEvent from "parog-web/models/abdominal-area-declared-journal-event";
import type AdverseEventDeclaredJournalEvent from "parog-web/models/adverse-event-declared-journal-event";
import type AlertProcessedJournalEvent from "parog-web/models/alert-processed-journal-event";
import type AlertTriggeredJournalEvent from "parog-web/models/alert-triggered-journal-event";
import type AppUsageConsentUpdatedJournalEvent from "parog-web/models/app-usage-consent-updated-journal-event";
import BdiIiDeclaredJournalEvent from "parog-web/models/bdi-ii-declared-journal-event";
import type BioExamExpectedDosageChangedJournalEvent from "parog-web/models/bio-exam-expected-dosage-changed-journal-event";
import type BioExamFrequencyChangedJournalEvent from "parog-web/models/bio-exam-frequency-changed-journal-event";
import type BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import type BioExamResult from "parog-web/models/bio-exam-result";
import type BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import type BiologicalActRefundedJournalEvent from "parog-web/models/biological-act-refunded-journal-event";
import type BloodPressureSessionStartedJournalEvent from "parog-web/models/blood-pressure-session-started-journal-event";
import type CapillarityBloodGlucoseMeasuredJournalEvent from "parog-web/models/capillarity-blood-glucose-measured-journal-event";
import type CareGiverAddedJournalEvent from "parog-web/models/care-giver-added-journal-event";
import type CnamProcessStartedJournalEvent from "parog-web/models/cnam-process-started-journal-event";
import type ComorbiditiesAndAllergiesChangedJournalEvent from "parog-web/models/comorbidities-and-allergies-changed-journal-event";
import type CompoundSurvey from "parog-web/models/compound-survey";
import type CompoundSurveyCompletedJournalEvent from "parog-web/models/compound-survey-completed-journal-event";
import type CompoundSurveyFrequenciesChangedJournalEvent from "parog-web/models/compound-survey-frequencies-changed-journal-event";
import CompoundSurveyFrequency from "parog-web/models/compound-survey-frequency";
import type ConnectedDeviceAcquiredJournalEvent from "parog-web/models/connected-device-acquired-journal-event";
import type ConnectedDeviceLinkedJournalEvent from "parog-web/models/connected-device-linked-journal-event";
import type ConsultationPlannedJournalEvent from "parog-web/models/consultation-planned-journal-event";
import type ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import Creator from "parog-web/models/creator";
import CrisisPlanDeclaredJournalEvent from "parog-web/models/crisis-plan-declared-journal-event";
import type CrohnSurveyFilledJournalEvent from "parog-web/models/crohn-survey-filled-journal-event";

import type DataSharingConsentUpdatedJournalEvent from "parog-web/models/data-sharing-consent-updated-journal-event";
import type DiabetowiseDischargeCompletedJournalEvent from "parog-web/models/diabetowise-discharge-completed-journal-event";
import type DiabguideRecommendationComputedJournalEvent from "parog-web/models/diabguide-recommendation-computed-journal-event";
import type DisorderHistoryDeclaredJournalEvent from "parog-web/models/disorder-history-declared-journal-event";
import type DmpAccessConsentUpdatedJournalEvent from "parog-web/models/dmp-access-consent-updated-journal-event";
import type DmpRetrievalSuccessfulJournalEvent from "parog-web/models/dmp-retrieval-successful-journal-event";
import type {
  default as DpSessionDeclaredJournalEvent,
  default as dpSessionDeclaredJournalEvent
} from "parog-web/models/dp-session-declared-journal-event";
import type DrugsAndDevicesRefundedJournalEvent from "parog-web/models/drugs-and-devices-refunded-journal-event";
import type FastDeclaredJournalEvent from "parog-web/models/fast-declared-journal-event";
import type FeelingEvasDeclaredJournalEvent from "parog-web/models/feeling-evas-declared-journal-event";
import type FileAttachment from "parog-web/models/file-attachment";
import type FileUploadedJournalEvent from "parog-web/models/file-uploaded-journal-event";
import type FollowUpReason from "parog-web/models/follow-up-reason";
import type GenericPrescriptionUploadedJournalEvent from "parog-web/models/generic-prescription-uploaded-journal-event";
import type HealthCentre from "parog-web/models/health-centre";
import type HealthCentreCohort from "parog-web/models/health-centre-cohort";
import type { HealthCentreRole } from "parog-web/models/health-centre-cohort";
import type HeightMeasuredJournalEvent from "parog-web/models/height-measured-journal-event";
import type HemodialysisSessionDeclaredJournalEvent from "parog-web/models/hemodialysis-session-declared-journal-event";
import type HospitalisationRefundedJournalEvent from "parog-web/models/hospitalisation-refunded-journal-event";
import type IbdDiskSurveyFilledJournalEvent from "parog-web/models/ibd-disk-survey-filled-journal-event";
import type jobsDeclaredJournalEvent from "parog-web/models/jobs-declared-journal-event";
import type {
  default as JournalEvent,
  default as JournalEventModel
} from "parog-web/models/journal-event";
import type KfreFiveYearsComputedJournalEvent from "parog-web/models/kfre-five-years-computed-journal-event";
import type KfreTwoYearsComputedJournalEvent from "parog-web/models/kfre-two-years-computed-journal-event";
import type LifestyleDeclaredJournalEvent from "parog-web/models/lifestyle-declared-journal-event";
import type BioExamObservation from "parog-web/models/loinc";
import type Medic from "parog-web/models/medic";
import type MedicalActFrequencyChangedJournalEvent from "parog-web/models/medical-act-frequency-changed-journal-event";
import type MedicalActRefundedJournalEvent from "parog-web/models/medical-act-refunded-journal-event";
import type MedicalEventJournalEvent from "parog-web/models/medical-event-journal-event";
import type MedicalHistoryChangedJournalEvent from "parog-web/models/medical-history-changed-journal-event";
import type { MrcStageStringEnum } from "parog-web/models/medical-history-changed-journal-event";
import type MedicalImagingPrescribedJournalEvent from "parog-web/models/medical-imaging-prescribed-journal-event";
import type MedicalImagingRefundedJournalEvent from "parog-web/models/medical-imaging-refunded-journal-event";
import type MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import type MedicinesReconciliationChangedJournalEvent from "parog-web/models/medicines-reconciliation-changed-journal-event";
import type MedicsPatient from "parog-web/models/medics-patient";
import type MobilePassiveDataDailySnapshotJournalEvent from "parog-web/models/mobile-passive-data-daily-snapshot-journal-event";
import type MrcReplacementJournalEvent from "parog-web/models/mrc-replacement-journal-event";
import type OnlineConsultationReportWrittenJournalEvent from "parog-web/models/online-consultation-report-written-journal-event";
import type OxygeneSaturationDeclaredJournalEvent from "parog-web/models/oxygene-saturation-declared-journal-event";
import type ParamedicalCarePrescribedJournalEvent from "parog-web/models/paramedical-care-prescribed-journal-event";
import type PatientChatMessageSentJournalEvent from "parog-web/models/patient-chat-message-sent-journal-event";
import type PatientDisenrolledJournalEvent from "parog-web/models/patient-disenrolled-journal-event";
import type PatientEnrolledJournalEvent from "parog-web/models/patient-enrolled-journal-event";
import type PatientInformationTransmittedJournalEvent from "parog-web/models/patient-information-transmitted-journal-event";
import type PersonalNotesChangedJournalEvent from "parog-web/models/personal-notes-changed-journal-event";
import PhqNineDeclaredJournalEvent from "parog-web/models/phq-nine-declared-journal-event";
import type PrescriptionJournalEvent from "parog-web/models/prescription-journal-event";
import type ProIrcStatusUpdatedJournalEvent from "parog-web/models/pro-irc-status-updated-journal-event";
import type QidsDeclaredJournalEvent from "parog-web/models/qids-declared-journal-event";
import type RchSurveyFilledJournalEvent from "parog-web/models/rch-survey-filled-journal-event";
import type RecursiveTaskPlannedJournalEvent from "parog-web/models/recursive-task-planned-journal-event";
import type ReferentLabUpdatedJournalEvent from "parog-web/models/referent-lab-updated-journal-event";
import ReimbursementsRetrievedJournalEvent from "parog-web/models/reimbursements-retrieved-journal-event";
import type RespiratoryRateMeasuredJournalEvent from "parog-web/models/respiratory-rate-measured-journal-event";
import type SpirometerAssociatedJournalEvent from "parog-web/models/spirometer-associated-journal-event";
import StoolsNumberDeclaredJournalEvent from "parog-web/models/stools-number-declared-journal-event";
import type SurveyCompletedJournalEvent from "parog-web/models/survey-completed-journal-event";
import type SurveyRequestedJournalEvent from "parog-web/models/survey-requested-journal-event";
import type TaskPlannedJournalEvent from "parog-web/models/task-planned-journal-event";
import type TaskProcessedJournalEvent from "parog-web/models/task-processed-journal-event";
import type TemperatureMeasuredJournalEvent from "parog-web/models/temperature-measured-journal-event";
import type TherapeuticAssistanceSessionCompletedJournalEvent from "parog-web/models/therapeutic-assistance-session-completed-journal-event";
import type TherapeuticalArea from "parog-web/models/therapeutical-area";
import type ThreeMonthsGoalsJournalEvent from "parog-web/models/three-months-goals-journal-event";
import type ThymicEpisodeOccurredJournalEvent from "parog-web/models/thymic-episode-occurred-journal-event";
import type TlsActiveJournalEvent from "parog-web/models/tls-active-journal-event";
import type {
  default as TlsConsentJournalEvent,
  TlsConsentStringEnum
} from "parog-web/models/tls-consent-journal-event";
import type TlsPrescribedJournalEvent from "parog-web/models/tls-prescribed-journal-event";
import type TlsTrialPeriodValidatedJournalEvent from "parog-web/models/tls-trial-period-validated-journal-event";
import type TreatmentHistoryChangedJournalEvent from "parog-web/models/treatment-history-changed-journal-event";
import type TreatmentPrescriptionChangedJournalEvent from "parog-web/models/treatment-prescription-changed-journal-event";
import type VemsDeclaredJournalEvent from "parog-web/models/vems-declared-journal-event";
import type WeightMeasuredJournalEvent from "parog-web/models/weight-measured-journal-event";
import type YmrsDeclaredJournalEvent from "parog-web/models/ymrs-declared-journal-event";
import type SemeiaSessionService from "parog-web/services/session";
import type TermsOfUseService from "parog-web/services/terms-of-use";
import { activeJournalEvents } from "parog-web/utils/decorators";
import formatToISO8601Date from "parog-web/utils/format-to-iso8601-date";
import type { DialysisTypeStringEnum } from "parog-web/utils/imports/correspondance-tables";
import {
  getJesOfClass,
  getJesOfClassSince,
  getJesOfClassSinceYear,
  getJesSince,
  getLastJe,
  getLastJeOfClass
} from "parog-web/utils/journal-events";
import removeDiacritics from "parog-web/utils/remove-diacritics";
import { sortBy } from "parog-web/utils/sort";
import type ConstantMonitoringChangedJournalEvent from "./constant-monitoring-changed-journal-event";
import type ConsultationFrequencyChangedJournalEvent from "./consultation-frequency-changed-journal-event";
import type DeseaseHistoryJournalEvent from "./desease-history-journal-event";
import type DocumentRetrievedJournalEvent from "./document-retrieved-journal-event";
import type LgcConsultationReportFilledJournalEvent from "./lgc-consultation-report-filled-journal-event";
import type NotificationTriggeredJournalEvent from "./notification-triggered-journal-event";
import type TlsTrialPeriodJournalEvent from "./tls-trial-period-journal-event";

export type ContactType = "phone" | "email";

export type PatientMatchingOptions = {
  firstName: string;
  lastName: string;
  birthdate?: Date | string;
  birthday?: Date | string;
};
export default class Patient extends Creator {
  @service declare abilities: AbilitiesService;
  @service declare features: Features;
  @service declare session: SemeiaSessionService;
  @service declare termsOfUse: TermsOfUseService;

  // Mentalwise specific
  @hasMany("abdominal-area-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare abdominalAreaDeclaredJournalEvents: AbdominalAreaDeclaredJournalEvent[];
  @attr("number") declare acceptedTermsOfUseVersion: number;
  @attr("string") declare address: string;
  @activeJournalEvents
  @hasMany("adverse-event-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare adverseEventDeclaredJournalEvents: AdverseEventDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("alert-processed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare alertProcessedJournalEvents: AlertProcessedJournalEvent[];
  @activeJournalEvents
  @hasMany("alert-triggered-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare alertTriggeredJournalEvents: AlertTriggeredJournalEvent[];
  @belongsTo("app-usage-consent-updated-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare appUsageConsentUpdatedJournalEvent:
    | AppUsageConsentUpdatedJournalEvent
    | undefined;
  @attr({
    defaultValue() {
      return {};
    }
  })
  associatedData;
  @activeJournalEvents
  @hasMany("bio-exam-expected-dosage-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare bioExamExpectedDosageChangedJournalEvents: BioExamExpectedDosageChangedJournalEvent[];
  @activeJournalEvents
  @hasMany("bio-exam-frequency-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare bioExamFrequencyChangedJournalEvents: BioExamFrequencyChangedJournalEvent[];
  @activeJournalEvents
  @hasMany("bio-exam-prescribed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare bioExamPrescribedJournalEvents: BioExamPrescribedJournalEvent[];
  @activeJournalEvents
  @hasMany("bio-exam-results-received-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare bioExamResultsReceivedJournalEvents: BioExamResultsReceivedJournalEvent[];
  @activeJournalEvents
  @hasMany("biological-act-refunded-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare biologicalActRefundedJournalEvents: BiologicalActRefundedJournalEvent[];
  @attr("date-only") declare birthday: Date;
  @attr("string") declare birthFirstName: string;
  @attr("string") declare birthMunicipalityCode: string | undefined;
  @attr("string") declare birthName: string;
  @attr("string") declare birthPlace: string | undefined;
  @attr("number") declare birthrank: number;
  @activeJournalEvents
  @hasMany("blood-pressure-session-started-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare bloodPressureSessionStartedJournalEvents: BloodPressureSessionStartedJournalEvent[];
  @activeJournalEvents
  @hasMany("capillarity-blood-glucose-measured-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare capillarityBloodGlucoseMeasuredJournalEvents: CapillarityBloodGlucoseMeasuredJournalEvent[];
  @activeJournalEvents
  @hasMany("care-giver-added-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare careGiverAddedJournalEvents: CareGiverAddedJournalEvent[];
  @attr("string") declare city: string;
  @belongsTo("cnam-process-started-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare cnamProcessStartedJournalEvent: CnamProcessStartedJournalEvent;
  @belongsTo("comorbidities-and-allergies-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare comorbiditiesAndAllergiesChangedJournalEvent:
    | ComorbiditiesAndAllergiesChangedJournalEvent
    | undefined;
  @activeJournalEvents
  @hasMany("compound-survey-completed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare compoundSurveyCompletedJournalEvents: CompoundSurveyCompletedJournalEvent[];
  @belongsTo("compound-survey-frequencies-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare compoundSurveyFrequenciesChangedJournalEvent:
    | CompoundSurveyFrequenciesChangedJournalEvent
    | undefined;
  @attr("string") declare computedNationalHealthNumber: string;
  @activeJournalEvents
  @hasMany("connected-device-acquired-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare connectedDeviceAcquiredJournalEvents: ConnectedDeviceAcquiredJournalEvent[];
  @activeJournalEvents
  @hasMany("connected-device-linked-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare connectedDeviceLinkedJournalEvents: ConnectedDeviceLinkedJournalEvent[];
  @belongsTo("constant-monitoring-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare constantMonitoringChangedJournalEvent?: ConstantMonitoringChangedJournalEvent;
  @belongsTo("consultation-frequency-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare consultationFrequencyChangedJournalEvent: ConsultationFrequencyChangedJournalEvent;
  @activeJournalEvents
  @hasMany("consultation-planned-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare consultationPlannedJournalEvents: ConsultationPlannedJournalEvent[];
  @activeJournalEvents
  @hasMany("consultation-report-written-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare consultationReportWrittenJournalEvents: ConsultationReportWrittenJournalEvent[];
  @attr() declare contactPreferences: ContactType[];
  @attr("date") declare createdAt: Date;
  @belongsTo("crisis-plan-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare crisisPlanDeclaredJournalEvent: CrisisPlanDeclaredJournalEvent;
  @activeJournalEvents
  @hasMany("crohn-survey-filled-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare crohnSurveyFilledJournalEvents: CrohnSurveyFilledJournalEvent[];
  @belongsTo("data-sharing-consent-updated-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare dataSharingConsentUpdatedJournalEvent:
    | DataSharingConsentUpdatedJournalEvent
    | undefined;
  @activeJournalEvents
  @hasMany("desease-history-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare deseaseHistoryJournalEvents: DeseaseHistoryJournalEvent[];
  @belongsTo("diabetowise-discharge-completed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare diabetowiseDischargeCompletedJournalEvent: DiabetowiseDischargeCompletedJournalEvent;
  @activeJournalEvents
  @hasMany("diabguide-recommendation-computed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare diabguideRecommendationComputedJournalEvents: DiabguideRecommendationComputedJournalEvent[];
  @attr("string") declare dialysisType: DialysisTypeStringEnum;
  /**********************************/
  /*          JOURNAL EVENTS        */
  /**********************************/
  @belongsTo("disorder-history-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare disorderHistoryDeclaredJournalEvent: DisorderHistoryDeclaredJournalEvent;
  @belongsTo("dmp-access-consent-updated-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare dmpAccessConsentUpdatedJournalEvent:
    | DmpAccessConsentUpdatedJournalEvent
    | undefined;
  @belongsTo("dmp-retrieval-successful-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare dmpRetrievalSuccessfulJournalEvent: DmpRetrievalSuccessfulJournalEvent;
  @activeJournalEvents
  @hasMany("document-retrieved-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare documentRetrievedJournalEvents: DocumentRetrievedJournalEvent[];
  @hasMany("dp-session-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare dpSessionDeclaredJournalEvents: dpSessionDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("drugs-and-devices-refunded-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare drugsAndDevicesRefundedJournalEvents: DrugsAndDevicesRefundedJournalEvent[];
  @attr("string") declare email: string;
  @attr("string") declare enrollmentStatus: string;
  @activeJournalEvents
  @hasMany("fast-declared-journal-event", { async: false, inverse: "patient" })
  declare fastDeclaredJournalEvents: FastDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("feeling-evas-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare feelingEvasDeclaredJournalEvents: FeelingEvasDeclaredJournalEvent[];
  @hasMany("file-attachment", {
    async: false,
    inverse: "patient"
  })
  declare fileAttachments: FileAttachment[];
  @activeJournalEvents
  @hasMany("file-uploaded-journal-event", { async: false, inverse: "patient" })
  declare fileUploadedJournalEvents: FileUploadedJournalEvent[];
  @attr("string") declare firstBirthFirstName: string;
  @attr("string") declare followUpReason: string | null;
  @attr("string") declare gender: "male" | "female";
  @activeJournalEvents
  @hasMany("generic-prescription-uploaded-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare genericPrescriptionUploadedJournalEvents: GenericPrescriptionUploadedJournalEvent[];
  @attr("boolean") declare hasPreferredUnitCachePending: boolean;
  @hasMany("health-centre-cohort", { async: false, inverse: "patient" })
  declare healthCentreCohorts: HealthCentreCohort[];
  @activeJournalEvents
  @hasMany("height-measured-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare heightMeasuredJournalEvents: HeightMeasuredJournalEvent[];
  // Nephrowise specific
  @hasMany("hemodialysis-session-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare hemodialysisSessionDeclaredJournalEvents: HemodialysisSessionDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("hospitalisation-refunded-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare hospitalisationRefundedJournalEvents: HospitalisationRefundedJournalEvent[];
  @activeJournalEvents
  @hasMany("ibd-disk-survey-filled-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare ibdDiskSurveyFilledJournalEvents: IbdDiskSurveyFilledJournalEvent[];
  @attr("string") declare importedFollowUpReason: string | undefined;
  @attr("boolean") declare insDocumentIsQualifying: boolean;
  @attr("string") declare insIdentityDocument?: string;
  @attr("string") declare insKey: string;
  @attr("string") declare insOid: InsOid | undefined;
  @attr("string") declare insStatus: InsStatus;
  @attr("date") declare insuranceAldStartDate: Date;
  @attr("boolean") declare insuranceCmuc: boolean;
  @attr("string") declare insuredSocialSecurityNumber: string;
  @attr("string") declare insValue: string;
  @belongsTo("jobs-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare jobsDeclaredJournalEvent: jobsDeclaredJournalEvent;
  @hasMany("journal-event", {
    polymorphic: true,
    async: false,
    inverse: "patient"
  })
  declare journalEvents: JournalEvent[];
  @activeJournalEvents
  @hasMany("kfre-five-years-computed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare kfreFiveYearsComputedJournalEvents: KfreFiveYearsComputedJournalEvent[];
  @activeJournalEvents
  @hasMany("kfre-two-years-computed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare kfreTwoYearsComputedJournalEvents: KfreTwoYearsComputedJournalEvent[];
  @activeJournalEvents
  @hasMany("lgc-consultation-report-filled-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare lgcConsultationReportFilledJournalEvents: LgcConsultationReportFilledJournalEvent[];
  @belongsTo("lifestyle-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare lifestyleDeclaredJournalEvent: LifestyleDeclaredJournalEvent;
  @belongsTo("medical-act-frequency-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicalActFrequencyChangedJournalEvent: MedicalActFrequencyChangedJournalEvent;
  @activeJournalEvents
  @hasMany("medical-act-refunded-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicalActRefundedJournalEvents: MedicalActRefundedJournalEvent[];
  @activeJournalEvents
  @hasMany("medical-event-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicalEventJournalEvents: MedicalEventJournalEvent[];
  @belongsTo("medical-history-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicalHistoryChangedJournalEvent:
    | MedicalHistoryChangedJournalEvent
    | undefined;
  @activeJournalEvents
  @hasMany("medical-imaging-prescribed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicalImagingPrescribedJournalEvents: MedicalImagingPrescribedJournalEvent[];
  @activeJournalEvents
  @hasMany("medical-imaging-refunded-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicalImagingRefundedJournalEvents: MedicalImagingRefundedJournalEvent[];
  @activeJournalEvents
  @hasMany("medication-prescribed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicationPrescribedJournalEvents: MedicationPrescribedJournalEvent[];
  @activeJournalEvents
  @hasMany("medicines-reconciliation-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare medicinesReconciliationChangedJournalEvents: MedicinesReconciliationChangedJournalEvent[];
  @hasMany("medic", { async: false, inverse: null })
  declare medics: Medic[];
  @hasMany("medics-patient", { async: false, inverse: "patient" })
  declare medicsPatients: MedicsPatient[];
  @belongsTo("mobile-passive-data-daily-snapshot-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare mobilePassiveDataDailySnapshotJournalEvent: MobilePassiveDataDailySnapshotJournalEvent;
  @attr("string") declare moreAddressInformation: string;
  @belongsTo("mrc-replacement-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare mrcReplacementJournalEvent: MrcReplacementJournalEvent | undefined;
  @attr("string") declare mrcStage: MrcStageStringEnum;
  @activeJournalEvents
  @hasMany("notification-triggered-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare notificationTriggeredJournalEvents: NotificationTriggeredJournalEvent[];
  @attr("string") declare occupation: string;
  @activeJournalEvents
  @hasMany("online-consultation-report-written-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare onlineConsultationReportWrittenJournalEvents: OnlineConsultationReportWrittenJournalEvent[];
  @activeJournalEvents
  @hasMany("oxygene-saturation-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare oxygeneSaturationDeclaredJournalEvents: OxygeneSaturationDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("paramedical-care-prescribed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare paramedicalCarePrescribedJournalEvents: ParamedicalCarePrescribedJournalEvent[];
  @attr("date") declare passwordChangedAt: Date;
  @activeJournalEvents
  @hasMany("patient-chat-message-sent-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare patientChatMessageSentJournalEvents: PatientChatMessageSentJournalEvent[];
  @belongsTo("patient-disenrolled-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare patientDisenrolledJournalEvent:
    | PatientDisenrolledJournalEvent
    | undefined;
  @belongsTo("patient-enrolled-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare patientEnrolledJournalEvent: PatientEnrolledJournalEvent | undefined;
  @belongsTo("patient-information-transmitted-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare patientInformationTransmittedJournalEvent: PatientInformationTransmittedJournalEvent;
  @belongsTo("personal-notes-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare personalNotesChangedJournalEvent:
    | PersonalNotesChangedJournalEvent
    | undefined;
  @attr("string") declare phone: string;
  @attr("string") declare postcode: string;
  @belongsTo("pro-irc-status-updated-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare proIrcStatusUpdatedJournalEvent:
    | ProIrcStatusUpdatedJournalEvent
    | undefined;
  @activeJournalEvents
  @hasMany("qids-declared-journal-event", { async: false, inverse: "patient" })
  declare qidsDeclaredJournalEvents: QidsDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("rch-survey-filled-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare rchSurveyFilledJournalEvents: RchSurveyFilledJournalEvent[];
  @activeJournalEvents
  @hasMany("recursive-task-planned-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare recursiveTaskPlannedJournalEvents: RecursiveTaskPlannedJournalEvent[];
  @belongsTo("referent-lab-updated-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare referentLabUpdatedJournalEvent: ReferentLabUpdatedJournalEvent;
  @attr("boolean") declare refreshTokensEnabled: boolean;
  @activeJournalEvents
  @hasMany("respiratory-rate-measured-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare respiratoryRateMeasuredJournalEvents: RespiratoryRateMeasuredJournalEvent[];
  @attr("string") declare socialSecurityNumber: string;
  @belongsTo("spirometer-associated-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare spirometerAssociatedJournalEvent:
    | SpirometerAssociatedJournalEvent
    | undefined;
  @activeJournalEvents
  @hasMany("stools-number-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare stoolsNumberDeclaredJournalEvents: StoolsNumberDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("survey-completed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare surveyCompletedJournalEvents: SurveyCompletedJournalEvent[];
  @activeJournalEvents
  @hasMany("survey-requested-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare surveyRequestedJournalEvents: SurveyRequestedJournalEvent[];
  @activeJournalEvents
  @hasMany("task-planned-journal-event", { async: false, inverse: "patient" })
  declare taskPlannedJournalEvents: TaskPlannedJournalEvent[];
  @activeJournalEvents
  @hasMany("task-processed-journal-event", { async: false, inverse: "patient" })
  declare taskProcessedJournalEvents: TaskProcessedJournalEvent[];
  @activeJournalEvents
  @hasMany("temperature-measured-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare temperatureMeasuredJournalEvents: TemperatureMeasuredJournalEvent[];
  @activeJournalEvents
  @hasMany("therapeutic-assistance-session-completed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare therapeuticAssistanceSessionCompletedJournalEvents: TherapeuticAssistanceSessionCompletedJournalEvent[];
  @hasMany("three-months-goals-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare threeMonthsGoalsJournalEvents: ThreeMonthsGoalsJournalEvent[];
  @activeJournalEvents
  @hasMany("thymic-episode-occurred-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare thymicEpisodeOccurredJournalEvents: ThymicEpisodeOccurredJournalEvent[];
  @attr("boolean") declare tlsActive: boolean;
  @activeJournalEvents
  @hasMany("tls-active-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare tlsActiveJournalEvents: TlsActiveJournalEvent[];
  @activeJournalEvents
  @hasMany("tls-consent-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare tlsConsentJournalEvents: TlsConsentJournalEvent[];
  @attr("string") declare tlsConsentState: TlsConsentStringEnum;
  @activeJournalEvents
  @hasMany("tls-prescribed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare tlsPrescribedJournalEvents: TlsPrescribedJournalEvent[];
  @belongsTo("tls-trial-period-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare tlsTrialPeriodJournalEvent: TlsTrialPeriodJournalEvent | undefined;
  @belongsTo("tls-trial-period-validated-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare tlsTrialPeriodValidatedJournalEvent:
    | TlsTrialPeriodValidatedJournalEvent
    | undefined;
  @belongsTo("treatment-history-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare treatmentHistoryChangedJournalEvent:
    | TreatmentHistoryChangedJournalEvent
    | undefined;
  @activeJournalEvents
  @hasMany("treatment-prescription-changed-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare treatmentPrescriptionChangedJournalEvents: TreatmentPrescriptionChangedJournalEvent[];
  @attr("boolean") declare twoFactorAuthenticationEnabled: boolean;
  @attr("string") declare uuid: string;
  @activeJournalEvents
  @hasMany("vems-declared-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare vemsDeclaredJournalEvents: VemsDeclaredJournalEvent[];
  @activeJournalEvents
  @hasMany("weight-measured-journal-event", {
    async: false,
    inverse: "patient"
  })
  declare weightMeasuredJournalEvents: WeightMeasuredJournalEvent[];
  @activeJournalEvents
  @hasMany("ymrs-declared-journal-event", { async: false, inverse: "patient" })
  declare ymrsDeclaredJournalEvents: YmrsDeclaredJournalEvent[];

  get activeAbdominalAreaDeclaredJournalEvents() {
    return this.abdominalAreaDeclaredJournalEvents
      .filter(je => !je.isArchived)
      .toSorted(sortBy("occurredAt", "desc"));
  }

  get activeDpSessionDeclaredJournalEvents(): DpSessionDeclaredJournalEvent[] {
    return this.dpSessionDeclaredJournalEvents
      .filter(je => !je.isArchived)
      .toSorted(sortBy("occurredAt", "desc"));
  }

  get activeHemodialysisSessionDeclaredJournalEvents(): HemodialysisSessionDeclaredJournalEvent[] {
    return this.hemodialysisSessionDeclaredJournalEvents
      .filter(je => !je.isArchived)
      .toSorted(sortBy("occurredAt", "desc"));
  }

  get activeJournalEvents(): JournalEvent[] {
    return this.journalEvents.filter(je => !je.isArchived);
  }

  get activeThreeMonthsGoalsJournalEvents() {
    return this.threeMonthsGoalsJournalEvents
      .filter(je => !je.isArchived)
      .toSorted(sortBy("occurredAt", "desc"));
  }

  get age(): number {
    const years = dayjs().diff(this.birthday, "year");
    const birthday = dayjs(this.birthday).add(years, "year");
    // If the birthday for this year has not occurred yet, subtract 1 from the age
    return dayjs().isBefore(birthday) ? years - 1 : years;
  }

  get authorizesAppUsage(): boolean {
    return this.appUsageConsentUpdatedJournalEvent?.agreed ?? false;
  }

  get authorizesDmpAccess(): boolean {
    return !!this.dmpAccessConsentUpdatedJournalEvent?.agreed;
  }

  get availableSurveys(): CompoundSurvey[] {
    return this.healthCentre.surveys || [];
  }

  get bioExamExpectedDosageChangedJournalEvent():
    | BioExamExpectedDosageChangedJournalEvent
    | undefined {
    return getLastJe<BioExamExpectedDosageChangedJournalEvent>(
      this.bioExamExpectedDosageChangedJournalEvents.slice()
    );
  }

  get consultationFrequencies() {
    const je = this.consultationFrequencyChangedJournalEvent;
    return je ? je.consultationFrequencies : [];
  }

  get crisisPlan(): CrisisPlanDeclaredJournalEvent | undefined {
    return getLastJeOfClass(
      this.activeJournalEvents,
      CrisisPlanDeclaredJournalEvent
    );
  }

  get dmpAccessEnabled(): boolean {
    if (!this.authorizesDmpAccess) return false;

    return this.isEnrolled;
  }

  get draftDpSession() {
    return this.dpSessionDeclaredJournalEvents.find(session => session.isDraft);
  }

  get draftHemodialysisSession():
    | HemodialysisSessionDeclaredJournalEvent
    | undefined {
    return this.hemodialysisSessionDeclaredJournalEvents.find(
      session => session.isDraft
    );
  }

  get enabledSurveyFrequencies(): CompoundSurveyFrequency[] {
    return (
      this.compoundSurveyFrequenciesChangedJournalEvent
        ?.enabledSurveyFrequencies || []
    );
  }

  get enabledSurveys(): (CompoundSurvey | null)[] {
    return this.enabledSurveyFrequencies.map(freq =>
      this.store.peekRecord("compound-survey", freq.surveyId)
    );
  }

  get enabledVitalSigns() {
    return this.vitalSigns.filter(v => v.enabled);
  }

  get followUpReasonId(): FollowUpReason["id"] {
    return this.patientEnrolledJournalEvent?.followUpReason || "";
  }

  get followUpReasonRecord(): FollowUpReason | null {
    const id = `${this.followUpReasonId}`;
    if (id.length === 0) return null;

    return this.store.peekRecord("follow-up-reason", id);
  }

  get fullAddress(): ReturnType<typeof htmlSafe> | string | null {
    const { address, postcode, city, moreAddressInformation } = this;
    let fullAdd = "";
    if (!isBlank(address)) fullAdd += address;
    if (!isBlank(address) && (!isBlank(postcode) || !isBlank(city))) {
      fullAdd += "<br>";
    }
    if (!isBlank(postcode)) fullAdd += postcode;
    if (!isBlank(postcode) && !isBlank(city)) {
      fullAdd += " ";
    }
    if (!isBlank(city)) fullAdd += city;

    if (!isBlank(moreAddressInformation)) {
      fullAdd += "<br>" + this.moreAddressInformation;
    }

    return fullAdd === "" ? null : htmlSafe(fullAdd);
  }

  override get fullName(): string {
    if (
      this.birthName &&
      this.lastName &&
      this.birthName.localeCompare(this.lastName, "fr", {
        sensitivity: "base"
      })
    ) {
      return `${super.fullName} (${this.birthName.toUpperCase()})`;
    }
    return super.fullName;
  }

  get fullNameWithoutBirthName(): string {
    return super.fullName;
  }

  get hasBreastCancer(): boolean {
    return !!this.followUpReason && this.followUpReason.includes("breast");
  }

  /**
   * Check if the patient matches the given data.
   * return Is the patient matching the given data.
   */
  get hasCardioHeartFailure(): boolean {
    return this.followUpReasonId === "cardio-heart-failure";
  }

  get hasCardioPathology(): boolean {
    return this.followUpReasonId?.startsWith("cardio");
  }

  get hasDiabetoPathology(): boolean {
    return this.followUpReasonId?.startsWith("diabeto");
  }

  get hasDigestPathology(): boolean {
    return this.followUpReasonId?.startsWith("digest");
  }

  get hasHematoCancer(): boolean {
    return (
      this.followUpReason == "onco-lymphoma-cancer" ||
      this.followUpReason == "onco-myeloma-cancer"
    );
  }

  get hasHepatoPathology(): boolean {
    return this.followUpReasonId?.startsWith("hepato");
  }

  get hasMetastaticCancer(): boolean {
    return this.followUpReason == "onco-breast-cancer";
  }

  @cached
  get hasOngoingMiciSurveyProcedure(): boolean {
    return this.taskPlannedJournalEvents.some(
      event =>
        event.responsibleType === "patient" &&
        event.responsibleId === Number(this.id) &&
        (event.taskType === "measure-vital-sign-rch-survey" ||
          event.taskType === "measure-vital-sign-crohn-survey") &&
        event.remindResponsible === true &&
        dayjs().isBefore(event.occurredAt)
    );
  }

  get hasPulmonaryPathology(): boolean {
    return this.followUpReasonId?.startsWith("pneumo");
  }

  get hasRenalPathology(): boolean {
    return this.followUpReasonId?.startsWith("nephro");
  }

  get healthCentre(): HealthCentre {
    const hcp = this.getHealthCentreCohort("configurator");
    if (hcp) return hcp.healthCentre;
    throw `No Health Centre configurator for patient ${this.id}`;
  }

  set healthCentre(healthCentre: HealthCentre) {
    this.setHealthCentre(healthCentre, "configurator");
  }

  get healthCentreConfigurator(): HealthCentre | undefined {
    return this.getHealthCentreCohort("configurator")?.healthCentre;
  }

  set healthCentreConfigurator(healthCentre: HealthCentre) {
    this.healthCentre = healthCentre;
  }

  get healthCentreSupport(): HealthCentre | undefined {
    return this.getHealthCentreCohort("support")?.healthCentre;
  }

  set healthCentreSupport(healthCentre: HealthCentre | null) {
    this.setHealthCentre(healthCentre, "support");
  }

  get initials(): string {
    return [this.firstName?.charAt(0), this.lastName?.charAt(0)]
      .join("")
      .trim();
  }

  get ins(): INS | undefined {
    if (
      (this.insStatus == InsStatus.Qualified ||
        this.insStatus == InsStatus.Recovered) &&
      this.insOid &&
      this.birthMunicipalityCode
    ) {
      let sex: InsSex = InsSex.Unknown;
      switch (this.gender) {
        case "male":
          sex = InsSex.Male;
          break;
        case "female":
          sex = InsSex.Female;
          break;
      }

      return {
        ins: {
          value: `${this.insValue}${this.insKey}`,
          oid: this.insOid
        },
        birthGivenName: this.birthFirstName,
        birthName: this.birthName,
        sex,
        birthPlaceCode: this.birthMunicipalityCode,
        birthDate: formatToISO8601Date(this.birthday)
      };
    }

    return undefined;
  }

  get insRetrieved(): boolean {
    return [InsStatus.Recovered, InsStatus.Qualified].includes(this.insStatus);
  }

  get ippNumber(): string | undefined {
    return this.getHealthCentreCohort("configurator")?.ippNumber;
  }

  set ippNumberConfigurator(ippNumber: string) {
    this.setIppNumber(ippNumber, "configurator");
  }

  get ippNumberConfigurator(): string | undefined {
    return this.ippNumber;
  }

  get ippNumberSupport(): string | undefined {
    return this.getHealthCentreCohort("support")?.ippNumber;
  }

  set ippNumberSupport(ippNumber: string) {
    this.setIppNumber(ippNumber, "support");
  }

  get isAcceptedTermsOfUse(): boolean {
    return this.acceptedTermsOfUseVersion == this.termsOfUse.currentVersion;
  }

  get isCrohn(): boolean {
    return this.followUpReasonId === "digest-crohn";
  }

  /**
   * Patient specifically removed from the program?
   * This accessor is here because for some parogs, the inclusion doesn't have the same sense.
   */
  get isDisenrolled(): boolean {
    return Boolean(this.patientDisenrolledJournalEvent);
  }

  get isDonor(): boolean {
    return this.followUpReasonId === "nephro-donor";
  }

  /**
   * Is the patient already included in the program?
   * This accessor is here because for some parogs, the inclusion doesn't have the same sense.
   */
  get isEnrolled(): boolean {
    return !!this.patientEnrolledJournalEvent && !this.isDisenrolled;
  }

  get isGeneric(): boolean {
    return this.followUpReasonId?.startsWith("generic");
  }

  get isMrc(): boolean {
    return this.followUpReasonId === "nephro-mrc";
  }

  get isMrcBordeaux(): boolean {
    return this.healthCentre.isMrcBordeaux;
  }

  /**
   * Patient created, but not yet included (nor removed).
   * This accessor is here because for some parogs, the inclusion doesn't have the same sense.
   */
  get isNotYetEnrolled(): boolean {
    return !this.isEnrolled && !this.isDisenrolled;
  }

  get isProIrc(): boolean {
    return Boolean(this.proIrcStatusUpdatedJournalEvent?.included);
  }

  get isProIrcEligible(): boolean {
    return this.healthCentre.proIrc && this.followUpReason == "nephro-mrc";
  }

  get isRch(): boolean {
    return this.followUpReasonId === "digest-rch";
  }

  get isReadOnly(): boolean {
    return this.isDisenrolled || !this.patientAbility.canEdit;
  }

  get isRenalTransplanted(): boolean {
    return this.isTransplanted && this.hasRenalPathology;
  }

  get isTransplanted(): boolean {
    return this.followUpReasonId?.endsWith("transplant");
  }

  get lastAbdominalAreaDeclaredJournalEvent():
    | AbdominalAreaDeclaredJournalEvent
    | undefined {
    return this.activeAbdominalAreaDeclaredJournalEvents[0];
  }

  get lastBdiIiDeclaredJournalEvent(): BdiIiDeclaredJournalEvent | undefined {
    return this.getLastJeOfClass(BdiIiDeclaredJournalEvent);
  }

  get lastBioExamResultsReceivedJournalEvent():
    | BioExamResultsReceivedJournalEvent
    | undefined {
    return getLastJe(this.bioExamResultsReceivedJournalEvents);
  }

  get lastBloodPressureSessionStartedJournalEvent():
    | BloodPressureSessionStartedJournalEvent
    | undefined {
    return getLastJe(this.bloodPressureSessionStartedJournalEvents);
  }

  get lastCapillarityBloodGlucoseMeasuredJournalEvent():
    | CapillarityBloodGlucoseMeasuredJournalEvent
    | undefined {
    return getLastJe(this.capillarityBloodGlucoseMeasuredJournalEvents);
  }

  get lastCompoundSurveyCompletedJournalEvent():
    | CompoundSurveyCompletedJournalEvent
    | undefined {
    return getLastJe(this.compoundSurveyCompletedJournalEvents);
  }

  get lastDiabguideRecommendationComputedJournalEvent():
    | DiabguideRecommendationComputedJournalEvent
    | undefined {
    return getLastJe<DiabguideRecommendationComputedJournalEvent>(
      this.diabguideRecommendationComputedJournalEvents.slice()
    );
  }

  get lastDmpUpdateDate(): Date | undefined {
    return this.lastReimbursementJournalEvent?.occurredAt;
  }

  get lastDpSessionDeclaredJournalEvent():
    | DpSessionDeclaredJournalEvent
    | undefined {
    return this.activeDpSessionDeclaredJournalEvents[0];
  }

  get lastFeelingEvasDeclaredJournalEvent():
    | FeelingEvasDeclaredJournalEvent
    | undefined {
    return getLastJe(this.feelingEvasDeclaredJournalEvents);
  }

  get lastHeightMeasuredJournalEvent(): HeightMeasuredJournalEvent | undefined {
    return getLastJe(this.heightMeasuredJournalEvents);
  }

  get lastHemodialysisSessionDeclaredJournalEvent():
    | HemodialysisSessionDeclaredJournalEvent
    | undefined {
    return this.activeHemodialysisSessionDeclaredJournalEvents[0];
  }

  get lastLgcConsultationReportFilledJournalEvent():
    | LgcConsultationReportFilledJournalEvent
    | undefined {
    return getLastJe(this.lgcConsultationReportFilledJournalEvents);
  }

  get lastMedicalEventJournalEvent(): MedicalEventJournalEvent | undefined {
    return getLastJe(this.medicalEventJournalEvents);
  }

  get lastMedicinesReconciliationChangedJournalEvent():
    | MedicinesReconciliationChangedJournalEvent
    | undefined {
    return getLastJe<MedicinesReconciliationChangedJournalEvent>(
      this.medicinesReconciliationChangedJournalEvents.slice()
    );
  }

  get lastOxygeneSaturationDeclaredJournalEvent():
    | OxygeneSaturationDeclaredJournalEvent
    | undefined {
    return getLastJe(this.oxygeneSaturationDeclaredJournalEvents);
  }

  get lastPasswordEditDate(): Date {
    return this.passwordChangedAt;
  }

  get lastPhqNineDeclaredJournalEvent():
    | PhqNineDeclaredJournalEvent
    | undefined {
    return this.getLastJeOfClass(PhqNineDeclaredJournalEvent);
  }

  get lastReimbursementJournalEvent():
    | ReimbursementsRetrievedJournalEvent
    | undefined {
    return getLastJeOfClass(
      this.activeJournalEvents,
      ReimbursementsRetrievedJournalEvent
    );
  }

  get lastRespiratoryRateMeasuredJournalEvent():
    | RespiratoryRateMeasuredJournalEvent
    | undefined {
    return getLastJe(this.respiratoryRateMeasuredJournalEvents);
  }

  get lastStoolsNumberDeclaredJournalEvent():
    | StoolsNumberDeclaredJournalEvent
    | undefined {
    return getLastJeOfClass(
      this.activeJournalEvents,
      StoolsNumberDeclaredJournalEvent
    );
  }

  get lastTemperatureMeasuredJournalEvent():
    | TemperatureMeasuredJournalEvent
    | undefined {
    return getLastJe(this.temperatureMeasuredJournalEvents);
  }

  get lastThreeMonthsGoalsJournalEvents():
    | ThreeMonthsGoalsJournalEvent
    | undefined {
    return this.activeThreeMonthsGoalsJournalEvents[0];
  }

  get lastTlsActiveJournalEvent(): TlsActiveJournalEvent | undefined {
    return getLastJe<TlsActiveJournalEvent>(
      this.tlsActiveJournalEvents.slice()
    );
  }

  get lastTlsConsentJournalEvent(): TlsConsentJournalEvent | undefined {
    return getLastJe<TlsConsentJournalEvent>(
      this.tlsConsentJournalEvents.slice()
    );
  }

  get lastTreatmentPrescriptionChangedJournalEvent():
    | TreatmentPrescriptionChangedJournalEvent
    | undefined {
    return getLastJe<TreatmentPrescriptionChangedJournalEvent>(
      this.treatmentPrescriptionChangedJournalEvents.slice()
    );
  }

  get lastVemsDeclaredJournalEvent(): VemsDeclaredJournalEvent | undefined {
    return getLastJe(this.vemsDeclaredJournalEvents);
  }

  get lastWeightMeasuredJournalEvent(): WeightMeasuredJournalEvent | undefined {
    return getLastJe(this.weightMeasuredJournalEvents);
  }

  get patientAbility(): PatientAbility {
    return this.abilities.abilityFor(
      "patient",
      this
    ) as unknown as PatientAbility;
  }

  get patientAge(): number {
    return dayjs().diff(this.birthday, "years");
  }

  get prescriptionJournalEvents(): PrescriptionJournalEvent[] {
    return [
      ...this.bioExamPrescribedJournalEvents.slice(),
      ...this.medicalImagingPrescribedJournalEvents.slice(),
      ...this.medicationPrescribedJournalEvents.slice(),
      ...this.paramedicalCarePrescribedJournalEvents.slice(),
      ...this.tlsPrescribedJournalEvents.slice()
    ];
  }

  get priorityBioExamLoincs(): string[] {
    const bioFrequencyJes = this.bioExamFrequencyChangedJournalEvents;

    if (!bioFrequencyJes.length) {
      const defaultPriorityExams = (
        this.therapeuticalArea as TherapeuticalArea
      ).priorityBioExams.map(exam => exam.id);

      return this.sortExamsByRank(defaultPriorityExams);
    }

    const ppsPriorityExams = [
      ...new Set(
        bioFrequencyJes.flatMap(je => {
          return je.loincs;
        })
      )
    ];

    return this.sortExamsByRank(ppsPriorityExams);
  }

  set referentMedic(medic: Medic) {
    const hcc = this.getHealthCentreCohort("configurator");
    if (!hcc) return;
    hcc.referentMedic = medic;
  }

  get referentMedic(): Medic | undefined {
    return this.getHealthCentreCohort("configurator")?.referentMedic;
  }

  get shouldChangePassword(): boolean {
    // If patient has never set its password return false
    if (!this.passwordChangedAt) return false;

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    // Otherwise return true if password edit date is one year old
    return this.passwordChangedAt < oneYearAgo;
  }

  get shouldShowDashboard(): boolean {
    if (this.therapeuticalArea?.id == "mental") {
      return true;
    }

    return this.isEnrolled;
  }

  get shouldShowHlaContent(): boolean {
    return (
      this.healthCentre.shouldShowHlaContent &&
      (this.isRenalTransplanted || this.isMrc)
    );
  }

  set supportReferentMedic(medic: Medic) {
    const hcc = this.getHealthCentreCohort("support");
    if (!hcc) return;
    hcc.referentMedic = medic;
  }

  get supportReferentMedic(): Medic | undefined {
    return this.getHealthCentreCohort("support")?.referentMedic;
  }

  /**
   * Return CompoundSurveyFrequencies for every avaliable CompoundSurvey
   */
  get surveyFrequencies(): CompoundSurveyFrequency[] {
    // Find or create a SurveyFrequency for each available CompoundSurvey
    return this.availableSurveys.map(
      survey =>
        this.enabledSurveyFrequencies.find(f => f.surveyId == survey.id) ||
        new CompoundSurveyFrequency(survey)
    );
  }

  get therapeuticalArea():
    | ModelRegistry["therapeutical-area"]
    | null
    | undefined {
    return (
      this.followUpReasonRecord?.therapeuticalArea ||
      this.healthCentre.therapeuticalArea
    );
  }

  get tlsAgreedAt(): Date | undefined {
    if (this.tlsRefused) return;
    return this.lastTlsActiveJournalEvent?.occurredAt;
  }

  get tlsEligible(): boolean {
    return ["nephro-transplant", "nephro-mrc"].includes(this.followUpReasonId);
  }

  get tlsRefused(): boolean {
    return !this.tlsActive;
  }

  get vitalSigns() {
    return this.constantMonitoringChangedJournalEvent?.constants || [];
  }

  bmiAt(date: Date): number | null {
    const lastWeightMeasuredAtDate = this.weightMeasuredJournalEvents
      ?.filter(je => dayjs(je.occurredAt).isSameOrBefore(date))
      .sort(sortBy("occurredAt", "desc"))[0] as WeightMeasuredJournalEvent;

    if (!lastWeightMeasuredAtDate) return null;
    return lastWeightMeasuredAtDate.bmi;
  }

  /**
   * Disenroll the patient.
   *
   * @param reason - The reason of the disenrollment. Free text, but a
   *   key is recommended.
   */
  async disenroll(
    reason: string,
    occurredAt = new Date()
  ): Promise<PatientDisenrolledJournalEvent> {
    const disenrolled = this.store.createRecord(
      "patient-disenrolled-journal-event",
      {
        patient: this,
        occurredAt,
        reason
      }
    );
    return disenrolled.save();
  }

  getHealthCentreCohort(
    role: HealthCentreRole
  ): HealthCentreCohort | undefined {
    return this.healthCentreCohorts.find(hcp => hcp.role === role);
  }

  getJesAt<T extends JournalEventModel>(
    journalEventClass: { new (): T },
    day: Date,
    ordering: "desc" | "asc" = "desc"
  ): Array<T> {
    const date = new Date(day).setHours(0, 0, 0, 0);

    return getJesOfClass<T>(
      this.activeJournalEvents,
      journalEventClass,
      ordering
    ).filter(je => new Date(je.occurredAt).setHours(0, 0, 0, 0) == date);
  }

  getJesOfClass<T extends JournalEventModel>(
    journalEventClass: { new (): T },
    ordering: "desc" | "asc" = "desc"
  ): Array<T> {
    const journalEventRelation = get(
      this,
      // @ts-expect-error Will be fixed later
      `${journalEventClass.type.replace(/-./g, m => m[1].toUpperCase())}s`
    ) as T[];

    if (journalEventRelation) {
      return journalEventRelation;
    }

    return getJesOfClass<T>(
      this.activeJournalEvents,
      journalEventClass,
      ordering
    );
  }

  getJesOfClassSince<T extends JournalEventModel>(
    journalEventClass: { new (): T },
    date: Date,
    ordering: "desc" | "asc" = "desc"
  ): Array<T> {
    return getJesOfClassSince<T>(
      this.journalEvents,
      journalEventClass,
      date,
      ordering
    );
  }

  getJesOfClassSinceYear<T extends JournalEventModel>(
    journalEventClass: { new (): T },
    numberOfYear?: number,
    ordering: "desc" | "asc" = "desc"
  ): Array<T> {
    /* The aim in the end is to directly call this method with the patient's relationship and
     * get rid of the `getJesOfClassSinceYear` call.
     * So this is temporary.
     */
    const journalEventRelation = get(
      this,
      // @ts-expect-error Will be fixed later
      `${journalEventClass.type.replace(/-./g, m => m[1].toUpperCase())}s`
    ) as T[];

    if (journalEventRelation) {
      return getJesSince<T>(
        journalEventRelation,
        dayjs().subtract(1, "y").toDate()
      );
    }

    return getJesOfClassSinceYear<T>(
      this.journalEvents,
      journalEventClass,
      numberOfYear,
      ordering
    );
  }

  getLastBioExamResultWithDate(
    code: string
  ): { result: BioExamResult; occurredAt: Date } | undefined {
    const je = this.bioExamResultsReceivedJournalEvents
      .sort(sortBy("occurredAt", "desc"))
      .find(je => je.result(code));
    return je && je.result(code)
      ? { result: je.result(code)!, occurredAt: je.occurredAt }
      : undefined;
  }

  /**
   * Find the last JournalEvent which is an instance of the given Class.
   *
   * @param klass - The class of the journal events you are searching for.
   * @return Last JournalEvent corresponding to the given Class.
   */
  getLastJeOfClass<T extends JournalEventModel>(klass: {
    new (): T;
  }): T | undefined {
    return getLastJeOfClass(this.activeJournalEvents, klass);
  }

  /**
   * Checks if patient SSN and given SSN are similar.
   *
   * "Similar" means they have the same numbers but may not have the same letter at the end.
   * A letter can appear when the SSN comes from the DMP: a letter D, T or N is put at the end
   * to indicate if the SSN comes from a Development, Test or productionN environment.
   *
   * Only the compared SSN is expected to have a letter, not the patient.
   *
   * @param ssn
   */
  hasSimilarSocialSecurityNumber(ssn: string): boolean {
    return (
      Boolean(this.socialSecurityNumber) &&
      (this.socialSecurityNumber === ssn ||
        this.socialSecurityNumber === ssn.slice(0, -1))
    );
  }

  isMatching(options: PatientMatchingOptions): boolean {
    const { firstName, lastName, birthdate } = options;
    let { birthday } = options;

    function clearTrait(trait) {
      return removeDiacritics(trait).toLowerCase();
    }

    birthday = birthday === undefined ? birthdate : birthday;
    const areBirthdayEqual =
      (!birthday && !this.birthday) ||
      (!!birthday &&
        !!this.birthday &&
        dayjs(this.birthday).format("YYYYMMDD") ===
          dayjs(birthday).format("YYYYMMDD"));

    return (
      Boolean(firstName) &&
      Boolean(lastName) &&
      clearTrait(this.firstName) === clearTrait(firstName) &&
      clearTrait(this.lastName) === clearTrait(lastName) &&
      areBirthdayEqual
    );
  }

  async processChatNotifications(): Promise<
    ModelRegistry["notification-processed-journal-event"][][]
  > {
    return Promise.all(
      this.patientChatMessageSentJournalEvents.map(mes =>
        mes.processNotifications()
      )
    );
  }

  setHealthCentre(healthCentre: HealthCentre | null, role: HealthCentreRole) {
    const hcp = this.getHealthCentreCohort(role);
    if (hcp?.healthCentre?.id == healthCentre?.id) return;
    if (healthCentre) {
      if (hcp) {
        hcp.healthCentre = healthCentre;
        hcp.ippNumber = undefined;
      } else {
        const hcp = this.store.createRecord("health-centre-cohort", {
          patient: this,
          healthCentre: healthCentre,
          role
        });

        this.healthCentreCohorts.push(hcp);
      }
    }
  }

  setIppNumber(ippNumber, role) {
    const hcp = this.getHealthCentreCohort(role);
    if (!hcp) return;
    hcp.ippNumber = ippNumber;
  }

  sortExamsByRank(loincs: string[]) {
    return loincs.toSorted(
      (loincA, loincB) =>
        (this.store.peekRecord("loinc", loincA) as BioExamObservation)?.rank -
        (this.store.peekRecord("loinc", loincB) as BioExamObservation)?.rank
    );
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    patient: Patient;
  }
}
